import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Anchor, Table } from "@mantine/core"
import { TFunction } from "i18next";
import { format } from "@react-input/mask";
import { IconEdit } from "@tabler/icons-react";
import { useSelector } from "react-redux";

import { Button } from "components";
import { AppConstants, AuthorityConstants, RouteConstants, ServiceConstants } from "constant";
import { ProfileResponse, UserRequest, UserResponse, UserStatus } from "types";
import { AppUtils, UserUtils } from "utils";

import { IRootState } from "config/redux";
import UserForm from "../form";
import { UserService } from "services";

const { REQUEST_PENDING } = ServiceConstants;
const { ROLE_USR_AGT } = AuthorityConstants;
const { MASK_SWISS_PHONE_NUMBER } = AppConstants;

interface UsersListItemProp {
  item: UserResponse;
  profiles: ProfileResponse[];
  t: TFunction;
  fetch: () => Promise<void>;
}

const UsersListItem = ({ item, fetch, profiles, t }: UsersListItemProp) => {
  const navigate = useNavigate();

  const getStatusColor = (status: UserStatus) => (
    status === 'ACTIVE' ? 'var(--mantine-color-primary-9)'
    : status === 'SUSPENDED' ? 'var(--mantine-color-error)'
    : ''
  );

  return (
    <>
      <Table.Tr key={item.id}>
        <Table.Td>
          {UserUtils.hasAuthority(ROLE_USR_AGT) ? (
            <Anchor c="green" onClick={() => navigate(`${RouteConstants.ROUTE_PROFILE}/${item.id}`, { state: { user: item } })}>
              {item.fullName}
            </Anchor>
          ) : (
            item.fullName
          )}
        </Table.Td>
        <Table.Td visibleFrom={'md'}>{item.countryCode && item.phoneNumber ? (item.countryCode + ' ' + item.phoneNumber) : ''}</Table.Td>
        <Table.Td>{item.email}</Table.Td>
        <Table.Td c={getStatusColor(item.status)}>{t('user.status.' + item.status)}</Table.Td>
        <Table.Td ta={'right'}>
          <UserEditModal item={item} fetch={fetch} profiles={profiles} t={t} />
        </Table.Td>
      </Table.Tr>
    </>
  )
}

const UserEditModal = ({ item, fetch, profiles, t }: UsersListItemProp) => {
  const [opened, setOpened] = useState<boolean>(false);
  const requestId = UserService.getUsersUpdateRequestId(item.id);
  const serviceObject = useSelector((state: IRootState) => state.service.object[requestId]);

  useEffect(() => {
    AppUtils.resolveServiceObject(serviceObject, requestId, async () => {
      await fetch();
      setOpened(false);
    })
  }, [serviceObject, requestId, fetch]);

  const onSubmit = useCallback((data: UserRequest) => UserService.usersUpdate(item.id, data), [item]);
  const isLoading = useMemo(() => serviceObject && serviceObject.status === REQUEST_PENDING, [serviceObject]);

  if (!UserUtils.hasAuthority(AuthorityConstants.ROLE_USR_U)) {
    return <></>;
  }

  return (
    <>
      <Button variant={'outline'} size={'compact-xs'} onClick={() => setOpened(true)}>
        <IconEdit size={14} />
      </Button>
      <UserForm
        item={{
          ...item,
          firstName: item.firstName ?? item.fullName.split(' ')[0],
          lastName: item.lastName ?? item.fullName.split(' ').slice(1).join(' '),
          phoneNumber: item.phoneNumber
            ? format(item.phoneNumber, {
              mask: MASK_SWISS_PHONE_NUMBER,
              replacement: { _: /\d/ },
            })
            : '',
          teamLeadId: item.teamLead?.id ?? null,
        } as UserRequest}
        loading={isLoading}
        opened={opened}
        onClose={() => setOpened(false)}
        onSubmit={onSubmit}
        profiles={profiles}
        t={t}
        title={t('user.modal.edit.any')}
      />
    </>
  )
}

export default UsersListItem;
